import type {BranchesPagination, VendorBranch} from '../../types';

import {BottomSheet, Button, Input, Loading, Radio} from '@ds/components';
import emptyList from 'assets/images/empty-list.svg';
import {useEffect, useRef, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

type VendorFilter = {
  title: string;
  id: 'ALL' | 'ACTIVE' | 'DEACTIVATE';
  vendorStatus?: 'active' | 'deactive';
};

type VendorSelectorBottomSheetProps = {
  show: boolean;
  branches: VendorBranch[];
  pagination: BranchesPagination;
  page: number;
  loading: boolean;
  selectedVendorId?: string | number;
  onClose: () => void;
  onSelect: (vendorId: string) => void;
  onLoadMore: () => void;
  onSearch: (value: string) => void;
  onFilter: (vendorStatus?: VendorFilter['vendorStatus']) => void;
};

const FILTERS: VendorFilter[] = [
  {title: 'همه', id: 'ALL'},
  {title: 'فعال', id: 'ACTIVE', vendorStatus: 'active'},
  {title: 'غیرفعال', id: 'DEACTIVATE', vendorStatus: 'deactive'}
];

const VendorSelectorBottomSheet: React.FC<VendorSelectorBottomSheetProps> = ({
  show,
  branches,
  pagination,
  page,
  loading,
  selectedVendorId,
  onClose,
  onSelect,
  onLoadMore,
  onSearch,
  onFilter
}) => {
  const [chosenVendorId, setChosenVendorId] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilterId, setSelectedFilterId] = useState<VendorFilter['id']>('ALL');
  const listId = 'profile-vendor-list';
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (show) {
      setChosenVendorId(String(selectedVendorId ?? ''));
      setSearchValue('');
      setSelectedFilterId('ALL');
    }
  }, [selectedVendorId, show]);

  useEffect(
    () => () => {
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
      }
    },
    []
  );

  const handleSearch = (value: string) => {
    setSearchValue(value);
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    searchTimer.current = setTimeout(() => onSearch(value), 1000);
  };

  const handleFilter = (filter: VendorFilter) => {
    setSelectedFilterId(filter.id);
    onFilter(filter.vendorStatus);
  };

  return (
    <BottomSheet
      defaultHeader
      title="لیست فروشگاه‌های شما"
      show={show}
      onClose={onClose}
      contentStyle={{height: '75%', padding: 0}}
      footer={
        <Button
          fullWidth
          disabled={!chosenVendorId}
          onClick={() => onSelect(chosenVendorId)}
        >
          انتخاب فروشگاه
        </Button>
      }
      footerStyle={{padding: '10px 16px', backgroundColor: 'var(--color-white)'}}
    >
      <div className="flex h-full flex-col overflow-hidden bg-gray-02">
        <div className="flex flex-col gap-3 bg-white p-3">
          <div className="flex gap-2">
            {FILTERS.map((filter) => (
              <button
                type="button"
                key={filter.id}
                className={`rounded-[10px] border px-2 py-1 text-caption ${
                  selectedFilterId === filter.id
                    ? 'border-orange bg-orange-pale text-orange-deep'
                    : 'border-transparent bg-gray-02 text-gray-07'
                }`}
                onClick={() => handleFilter(filter)}
              >
                {filter.title}
              </button>
            ))}
          </div>
          <Input
            value={searchValue}
            placeholder="نام فروشگاه خود را جستجو کنید"
            allowClear
            onClear={() => handleSearch('')}
            onChange={(event) => handleSearch(event.target.value)}
          />
        </div>

        <div id={listId} className="min-h-0 flex-1 overflow-auto">
          {!loading && branches.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3">
              <img className="max-h-40" src={emptyList} alt="" />
              <span className="text-body-sm-emphasis text-gray-09">فروشگاه یافت نشد!</span>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={branches.length}
              next={onLoadMore}
              hasMore={(pagination.totalPage ?? 0) > page}
              loader={
                page !== 0 ? (
                  <div className="flex h-12 items-center justify-center bg-white">
                    <Loading size="medium" color="primary" />
                  </div>
                ) : null
              }
              scrollableTarget={listId}
            >
              {loading && page === 0 ? (
                <div className="flex h-20 items-center justify-center bg-white">
                  <Loading size="medium" color="primary" />
                </div>
              ) : (
                <Radio.Group
                  className="w-full"
                  value={chosenVendorId}
                  onChange={(event) => setChosenVendorId(event.target.value)}
                >
                  {branches.map((branch) => (
                    <div
                      className="border-b-2 border-gray-02 bg-white px-3 py-4"
                      key={branch.id}
                    >
                      <Radio value={String(branch.id)}>{branch.attributes.title}</Radio>
                    </div>
                  ))}
                </Radio.Group>
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>
    </BottomSheet>
  );
};

export default VendorSelectorBottomSheet;
